/**
 * Produit type SERVICE (catalogue vitrine) — payload Admin + visibilité sections.
 * ≠ Prestation / RDV (ServicesManager).
 */

/** Sections ProduitModal à masquer pour un SERVICE catalogue. */
export const SERVICE_HIDDEN_SECTION_KEYS = Object.freeze([
  'venteAuPoids',
  'planchePersonnes',
  'ingredients',
  'champsMetier',
  'stock',
  'compositionPieces',
  'options',
  'badges',
  'elementsComposables',
  'lienPlateaux',
  'templates',
]);

/** Sections communes toujours utiles pour un SERVICE. */
export const SERVICE_CORE_SECTION_KEYS = Object.freeze([
  'photos',
  'infos',
  'prix',
  'categorie',
]);

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isServiceTypeProduit(value) {
  return String(value || '').toUpperCase() === 'SERVICE';
}

/**
 * Visibilité section : PHYSIQUE = délégation showSec ; SERVICE = core only.
 * @param {string} typeProduit
 * @param {string} sectionKey
 * @param {(key: string) => boolean} showSecPhysical
 */
export function shouldShowSectionForProduitType(
  typeProduit,
  sectionKey,
  showSecPhysical
) {
  if (isServiceTypeProduit(typeProduit)) {
    return SERVICE_CORE_SECTION_KEYS.includes(sectionKey);
  }
  return typeof showSecPhysical === 'function'
    ? showSecPhysical(sectionKey)
    : true;
}

/**
 * Titre modal selon type + mode (vocabulaire commerçant O1).
 * SERVICE catalogue = Prestation sans réservation — pas « Service » technique.
 * @param {{ isService: boolean, isEdit: boolean }} opts
 */
export function getProduitModalTitle({ isService, isEdit }) {
  if (isService) {
    return isEdit ? 'Modifier la prestation' : 'Nouvelle prestation';
  }
  return isEdit ? 'Modifier le produit' : 'Nouveau produit';
}

/**
 * Extraire les IDs d’ingrédients baseline (préservation backend qui réécrit toujours les relations).
 * @param {object|null|undefined} produit
 * @returns {string[]}
 */
export function extractBaselineIngredientIds(produit) {
  if (!produit || !Array.isArray(produit.ingredients)) return [];
  const ids = [];
  for (const item of produit.ingredients) {
    const id =
      item?.ingredientId ||
      item?.ingredient?.id ||
      (typeof item?.id === 'string' && !item?.ingredient ? item.id : null);
    if (typeof id === 'string' && id) ids.push(id);
  }
  return ids;
}

/**
 * @param {unknown} priceStr
 * @param {(s: unknown) => number|null} convertPrice
 */
function parseOptionalPrice(priceStr, convertPrice) {
  if (priceStr === '' || priceStr == null) return null;
  return convertPrice(priceStr);
}

/**
 * Payload UPDATE SERVICE — whitelist + préservation des champs hors UI.
 * Ne pas inclure stock / composition / options / preparationRequis forcé null.
 *
 * @param {{
 *   formData: object,
 *   baselineProduit: object,
 *   commercantId: string,
 *   convertPrice: (s: unknown) => number|null,
 *   normalizeTauxTVA: (v: unknown, commercantType?: unknown) => number,
 *   commercantType?: unknown,
 *   includeAdvanced?: boolean,
 * }} args
 */
export function buildServiceProduitUpdatePayload({
  formData,
  baselineProduit,
  commercantId,
  convertPrice,
  normalizeTauxTVA,
  commercantType,
  includeAdvanced = true,
}) {
  const baseline = baselineProduit || {};
  const dureeRaw = formData.dureeMinutes;
  const dureeParsed =
    dureeRaw === '' || dureeRaw == null
      ? null
      : parseInt(String(dureeRaw), 10);

  /** @type {Record<string, unknown>} */
  const payload = {
    commercantId: commercantId || baseline.commercantId,
    typeProduit: 'SERVICE',
    nom: formData.nom,
    description: formData.description ?? '',
    prix: convertPrice(formData.prix) || 0,
    prixPromo: parseOptionalPrice(formData.prixPromo, convertPrice),
    categorie: formData.categorie || '',
    sousCategorie: formData.sousCategorie || null,
    images: Array.isArray(formData.images) ? formData.images : [],
    tauxTVA: normalizeTauxTVA(formData.tauxTVA, commercantType),
    dureeMinutes:
      dureeParsed != null && Number.isFinite(dureeParsed) ? dureeParsed : null,
    actif: formData.actif !== false,
    // Préservation relations ingrédients (le backend deleteMany systématique)
    ingredientIds: extractBaselineIngredientIds(baseline),
  };

  if (formData.indisponible !== undefined) {
    payload.indisponible = Boolean(formData.indisponible);
  } else if (baseline.indisponible !== undefined) {
    payload.indisponible = Boolean(baseline.indisponible);
  }

  if (includeAdvanced) {
    const minP = formData.minParticipants;
    const maxP = formData.maxParticipants;
    payload.minParticipants =
      minP === '' || minP == null ? null : parseInt(String(minP), 10) || null;
    payload.maxParticipants =
      maxP === '' || maxP == null ? null : parseInt(String(maxP), 10) || null;
    payload.equipementFourni = Boolean(formData.equipementFourni);
    // Ne forcer null que si l’utilisateur a touché le champ (string, y compris vide)
    if (typeof formData.preparationRequis === 'string') {
      const trimmed = formData.preparationRequis.trim();
      payload.preparationRequis = trimmed.length > 0 ? trimmed : null;
    }
  }

  return payload;
}

/**
 * Payload CREATE SERVICE catalogue (pas Prestation).
 */
export function buildServiceProduitCreatePayload({
  formData,
  commercantId,
  convertPrice,
  normalizeTauxTVA,
  commercantType,
}) {
  const dureeRaw = formData.dureeMinutes;
  const dureeParsed =
    dureeRaw === '' || dureeRaw == null
      ? null
      : parseInt(String(dureeRaw), 10);

  return {
    commercantId,
    typeProduit: 'SERVICE',
    nom: formData.nom,
    description: formData.description ?? '',
    prix: convertPrice(formData.prix) || 0,
    prixPromo: parseOptionalPrice(formData.prixPromo, convertPrice),
    categorie: formData.categorie || '',
    sousCategorie: formData.sousCategorie || null,
    images: Array.isArray(formData.images) ? formData.images : [],
    tauxTVA: normalizeTauxTVA(formData.tauxTVA, commercantType),
    dureeMinutes:
      dureeParsed != null && Number.isFinite(dureeParsed) ? dureeParsed : null,
    actif: formData.actif !== false,
    indisponible: Boolean(formData.indisponible),
    stock: -1,
    ingredientIds: [],
    ingredientsText: [],
    options: [],
    minParticipants:
      formData.minParticipants == null || formData.minParticipants === ''
        ? null
        : parseInt(String(formData.minParticipants), 10) || null,
    maxParticipants:
      formData.maxParticipants == null || formData.maxParticipants === ''
        ? null
        : parseInt(String(formData.maxParticipants), 10) || null,
    equipementFourni: Boolean(formData.equipementFourni),
    preparationRequis:
      typeof formData.preparationRequis === 'string' &&
      formData.preparationRequis.trim()
        ? formData.preparationRequis.trim()
        : null,
  };
}

/**
 * Vérifie qu’un payload SERVICE update ne touche pas les champs food/stock.
 * @param {Record<string, unknown>} payload
 */
export function assertServiceUpdatePayloadSafe(payload) {
  const forbidden = [
    'stock',
    'stockLimite',
    'modeComposition',
    'venteAuPoids',
    'productType',
    'allergenes',
    'vegetarien',
    'vegan',
    'sansGluten',
    'bio',
    'options',
    'contraintes',
    'ingredientIdsPlanche',
    'plateauxIdsDisponibles',
    'tempsPreparation',
    'isPlateauParPersonne',
  ];
  const hits = forbidden.filter((k) =>
    Object.prototype.hasOwnProperty.call(payload, k)
  );
  return { ok: hits.length === 0, hits };
}
